import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import HighlightText from '../WidgetEditComponents/HighlightText';
import { generateLayoutStyles, getResponsiveValue, appendPx, appendPercent } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function SimplisticQuote({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // ✅ DEBUG: Prüfe Widget-Daten
    console.log('🎨 SimplisticQuote rendered:', {
        widgetType: data?.type,
        layoutType: data?.layoutType,
        categoryText: data?.categoryText,
        showCategoryText: data?.showCategoryText,
        isEditMode
    });
    
    // ✅ INLINE EDITING STATES - EXAKT WIE HEROHEADER
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // ✅ RESPONSIVE LAYOUT DETECTION - NEW LAYOUT SYSTEM 2025
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // ✅ SIMPLIFIED: Container-basierte Breakpoint-Erkennung (Standard wie alle Widgets)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // Standard 750px Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // ✅ INLINE EDITING FUNCTIONS - EXAKT WIE HEROHEADER
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler - EXAKT WIE HEROHEADER
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            // Smarte Erkennung: Ist es ein echter Außenklick?
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            // Definiere was ein "echter Außenklick" ist
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                handleInlineCancel();
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // Layout-Messung beim Klick - EXAKT WIE HEROHEADER
    const handleElementClick = (e, fieldName, currentValue) => {
        e.preventDefault();
        e.stopPropagation();
        
        // LEAN Layout-Erfassung - nur das Nötige
        const rect = e.target.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(e.target);
        
        const layoutData = {
            width: rect.width + 'px',
            height: rect.height + 'px',
            // Spacing (wichtigste Eigenschaften)
            marginTop: computedStyle.marginTop,
            marginBottom: computedStyle.marginBottom,
            marginLeft: computedStyle.marginLeft,
            marginRight: computedStyle.marginRight
        };
        
        setOriginalLayout(layoutData);
        handleInlineEdit(fieldName, currentValue);
    };

    // ✅ RENDER EDITABLE TEXT FUNCTION - EXAKT 1:1 KOPIE VOM HEROHEADER + CSS Classes
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...', className = '') => {
        if (!isEditMode) {
            // ✅ BUTTON CLICK HANDLER für Public Mode - wie HeroHeader
            if (Element === 'button' && currentValue) {
                return (
                    <Element 
                        className={className}
                        style={{
                            ...style,
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                        onClick={handleButtonClick}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = style.boxShadow || 'none';
                        }}
                    >
                        {currentValue}
                    </Element>
                );
            }
            
            return currentValue ? (
                <Element className={className} style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button';
            
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
      
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        ref={(element) => {
                            if (element) {
                                // ✅ KRITISCH: Cursor ans ENDE setzen wie HeroHeader
                                setTimeout(() => {
                                    element.style.height = 'auto';
                                    element.style.height = element.scrollHeight + 'px';
                                    element.setSelectionRange(element.value.length, element.value.length);
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // ✅ EXAKT WIE HEROHEADER: Behält Original-Styles bei
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // Button Input Field
                <div style={{ 
                    position: 'relative',
                    width: layoutData.width || '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        ref={(element) => {
                            if (element) {
                                // ✅ KRITISCH: Cursor ans ENDE setzen wie HeroHeader
                                setTimeout(() => {
                                    element.setSelectionRange(element.value.length, element.value.length);
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: style.background || style.backgroundColor,
                            border: style.border || 'none',
                            borderRadius: style.borderRadius,
                            padding: style.padding,
                            width: layoutData.width || style.width,
                            height: layoutData.height || style.height,
                            minWidth: layoutData.width || style.minWidth,
                            maxWidth: layoutData.width || 'none',
                            minHeight: layoutData.height || 'auto',
                            maxHeight: layoutData.height || 'none',
                            marginTop: layoutData.marginTop || '0',
                            marginBottom: layoutData.marginBottom || '0',
                            marginLeft: layoutData.marginLeft || '0',
                            marginRight: layoutData.marginRight || '0',
                            display: style.display,
                            alignItems: style.alignItems,
                            justifyContent: style.justifyContent,
                            alignSelf: style.alignSelf,
                            boxShadow: 'none',
                            cursor: 'text',
                            textAlign: style.textAlign || 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        // ✅ NICHT EDITING: Klickbarer Text - EXAKT WIE HEROHEADER
        return (
            <Element
                className={className}
                onClick={(e) => handleElementClick(e, fieldName, currentValue)}
                style={{ 
                    ...style,
                    cursor: isSelected ? (Element === 'button' ? 'pointer' : 'text') : 'default',
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // ✅ BORDER STYLE FUNCTION - EXAKT WIE HEROHEADER
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // ✅ RESPONSIVE TEXT ALIGNMENT
    const getTextAlignment = () => {
        return isMobileLayout ? 
            (data.textAlignmentMobile || 'center') : 
            (data.textAlignment || 'center');
    };

    const getFlexAlignment = () => {
        const alignment = getTextAlignment();
        switch (alignment) {
            case 'center': return 'center';
            case 'right': return 'flex-end';
            default: return 'flex-start';
        }
    };

    // ✅ SIMPLIFIED LAYOUT SYSTEM - Nur noch image-top-centered für Mobile und Desktop
    const getLayoutConfig = () => {
        console.log('🎨 SimplisticQuote Simplified Layout System - Only image-top-centered:', {
            layoutType: 'image-top-centered',
            isMobileLayout
        });
        
        // ✅ EINHEITLICHES LAYOUT: Immer image-top-centered für optimale UX
        return {
            direction: 'column',
            showBackgroundImage: false,
            contentAlignment: 'center',
            textSpacing: '30px',
            imagePosition: 'top',
            imageFirst: true,
            buttonAlignment: 'center'
        };
    };

    const layoutConfig = getLayoutConfig();
    console.log('🎨 SimplisticQuote final layoutConfig:', layoutConfig);

    // ✅ RESPONSIVE WIDGET STYLES mit generateLayoutStyles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor || '#ffffff',
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none', // ✅ Widget-Container hat keine Max Width - Content-Container steuert das
        display: 'flex',
        flexDirection: 'column', // ✅ Immer Column-Layout
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: getTextAlignment()
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Property
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile: Template-Default
                (data.height || 600)         // Desktop: Template-Default
            )
        })
    };

    // ✅ DELETE HANDLER
    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // ✅ BUTTON CLICK HANDLER - wie HeroHeader
    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // ✅ ELEMENT ORDERING SYSTEM - Standard-Reihenfolge wenn nicht definiert
    const getElementOrder = () => {
        return data.elementOrder || {
            image: 1,
            categoryText: 2,
            richText: 3,
            simpleText: 4,
            button: 5
        };
    };

    // ✅ RENDER ELEMENTS IN ORDER - Dynamische Element-Reihenfolge
    const renderElementsInOrder = () => {
        const elementOrder = getElementOrder();
        
        // Erstelle Array mit allen verfügbaren Elementen
        const elements = [
            {
                key: 'image',
                order: elementOrder.image,
                component: (data.showContentImage !== false && data.optionalImage) ? (
                    <div key="image" className="simplistic-quote-image-container" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        // ✅ RESPONSIVE IMAGE MARGINS - verwendet bestehende Field-Namen
                        marginBottom: `${isMobileLayout ? (data.imageMarginMobile ?? 15) : (data.imageMargin ?? 20)}px`
                    }}>
                        <img 
                            className="simplistic-quote-content-image"
                            src={data.optionalImage}
                            alt="Content"
                            style={{
                                width: isMobileLayout ? 
                                    `${data.imageWidthMobile ?? 200}px` : 
                                    `${data.imageWidth ?? 300}px`,
                                height: 'auto'
                            }}
                        />
                    </div>
                ) : null
            },
            {
                key: 'categoryText',
                order: elementOrder.categoryText,
                component: (data.showCategoryText !== false && data.categoryText) ? (
                    <div key="categoryText"
                        className={`simplistic-quote-category-text quote-animated ${data.enableAnimations ? (data.animationType || 'fadeIn') : ''}`}
                        style={{
                            animationDuration: data.enableAnimations ? `${data.animationDuration || 600}ms` : '0ms',
                            animationDelay: data.enableAnimations ? `${data.animationDelay || 100}ms` : '0ms'
                        }}
                    >
                        {renderEditableText(
                            'categoryText',
                            data.categoryText,
                            'div',
                            categoryTextStyle,
                            'Click to edit category text...',
                            'simplistic-quote-category'
                        )}
                    </div>
                ) : null
            },
            {
                key: 'richText',
                order: elementOrder.richText,
                component: (data.showRichText !== false && data.richText) ? (
                    <div key="richText"
                        className={`simplistic-quote-header-text quote-animated ${data.enableAnimations ? (data.animationType || 'fadeInUp') : ''}`}
                        style={{
                            animationDuration: data.enableAnimations ? `${data.animationDuration || 600}ms` : '0ms',
                            animationDelay: data.enableAnimations ? `${data.animationDelay || 200}ms` : '0ms'
                        }}
                    >
                        {/* ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv */}
                        {(!data.richTextHighlightWords || data.richTextHighlightWords.trim() === '') ? (
                            // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                            renderEditableText(
                                'richText',
                                data.richText,
                                'div',
                                richTextStyle,
                                'Click to edit header text...',
                                'simplistic-quote-header'
                            )
                        ) : (
                            // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                            <div className="simplistic-quote-header-highlight" style={{
                                ...richTextStyle,
                                whiteSpace: 'pre-wrap'
                            }}>
                                <HighlightText
                                    text={data.richText}
                                    highlightWords={data.richTextHighlightWords}
                                    baseStyle={{}}
                                    highlightFont={data.richTextHighlightFont || fonts.globalSmallFont}
                                    highlightColor={data.richTextHighlightColor}
                                    highlightWeight={data.richTextHighlightWeight}
                                    highlightSize={isMobileLayout ? 
                                        (data.richTextHighlightSizeMobile || data.richTextSizeMobile || 28) : 
                                        (data.richTextHighlightSize || data.richTextSize || 44)
                                    }
                                    highlightSizeMobile={data.richTextHighlightSizeMobile || data.richTextSizeMobile || 28}
                                    highlightSizeBig={data.richTextHighlightSizeBig || data.richTextSizeBig || 52}
                                    highlightMarginLeft={data.richTextHighlightMarginLeft || 0}
                                    highlightMarginRight={data.richTextHighlightMarginRight || 0}
                                    highlightEffect={data.richTextHighlightEffect}
                                    getTextEffectStyles={getTextEffectStyles}
                                />
                            </div>
                        )}
                    </div>
                ) : null
            },
            {
                key: 'simpleText',
                order: elementOrder.simpleText,
                component: (data.showSimpleText !== false && data.simpleText) ? (
                    <div key="simpleText"
                        className={`simplistic-quote-paragraph-text quote-animated ${data.enableAnimations ? (data.animationType || 'fadeIn') : ''}`}
                        style={{
                            animationDuration: data.enableAnimations ? `${data.animationDuration || 600}ms` : '0ms',
                            animationDelay: data.enableAnimations ? `${(data.animationDelay || 200) + 100}ms` : '0ms'
                        }}
                    >
                        {renderEditableText(
                            'simpleText',
                            data.simpleText,
                            'div',
                            simpleTextStyle,
                            'Click to edit paragraph text...',
                            'simplistic-quote-paragraph'
                        )}
                    </div>
                ) : null
            },
            {
                key: 'button',
                order: elementOrder.button,
                component: (data.showButtonText !== false && data.buttonText) ? (
                    <div key="button" className="simplistic-quote-button-container" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        // ✅ INDIVIDUELLE BUTTON MARGINS
                        marginTop: `${data.buttonMarginTop ?? 30}px`,
                        marginBottom: `${data.buttonMarginBottom ?? 0}px`
                    }}>
                        {renderEditableText(
                            'buttonText',
                            data.buttonText,
                            'button',
                            {
                                // ✅ GRADIENT vs. SOLID COLOR (wie HeroHeader)
                                background: data.buttonGradientEnabled 
                                    ? `linear-gradient(135deg, ${data.buttonGradientColor1 || '#667eea'} 0%, ${data.buttonGradientColor2 || '#764ba2'} 100%)`
                                    : ((!data.buttonEffect || data.buttonEffect === 'none') 
                                        ? (data.buttonColor || '#667eea')
                                        : 'transparent'),
                                        
                                // ✅ GRADIENT ANIMATION
                                backgroundSize: data.buttonGradientEnabled && data.buttonGradientAnimation ? '300% 300%' : 'auto',
                                
                                // ✅ BUTTON EFFECTS (falls kein Gradient)
                                ...(!data.buttonGradientEnabled && getButtonEffectStyles(data.buttonEffect)),
                                
                                // ✅ TEXT COLOR
                                color: data.buttonTextColor || '#ffffff',
                                
                                // ✅ RESPONSIVE TYPOGRAPHY
                                fontSize: isMobileLayout ? appendPx(data.buttonTextSizeMobile || data.buttonTextSize || 16) : appendPx(data.buttonTextSize || 16),
                                fontWeight: isMobileLayout ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') : (data.buttonTextWeight || '600'),
                                fontFamily: data.buttonFont || fonts.globalSmallFont,
                                
                                // ✅ RESPONSIVE DIMENSIONS - Konsistentes ButtonWidgetStyle-System
                                width: isMobileLayout ? 
                                    // Mobile: Prozent-basiert (ButtonWidgetStyle-System)
                                    appendPercent(data.buttonWidthMobile || 90) : 
                                    // Desktop: Pixel-basiert (ButtonWidgetStyle-System)
                                    appendPx(data.buttonWidthDesktop || 200),
                                maxWidth: isMobileLayout ? appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 280) : appendPx(data.buttonMaxWidth || 300),
                                height: isMobileLayout ? appendPx(data.buttonHeightMobile || data.buttonHeight || 45) : appendPx(data.buttonHeight || 50),
                                borderRadius: isMobileLayout ? appendPx(data.buttonBorderRadiusMobile ?? data.buttonBorderRadius ?? 6) : appendPx(data.buttonBorderRadius ?? 8),
                                
                                // ✅ BORDER SYSTEM (ButtonWidgetStyle-kompatibel)
                                border: data.buttonBorderEnabled && data.buttonBorderWidth && data.buttonBorderWidth > 0
                                    ? `${data.buttonBorderWidth}px solid ${data.buttonBorderColor || '#5C80BC'}`
                                    : 'none',
                                
                                // ✅ ANIMATION SYSTEM (wie HeroHeader - Priorität: Gradient > Button > None)
                                animation: data.buttonGradientEnabled && data.buttonGradientAnimation 
                                    ? 'gradientShift 4s ease-in-out infinite' 
                                    : data.buttonAnimationsEnabled && data.buttonAnimationType && data.buttonAnimationType !== 'none'
                                    ? `${data.buttonAnimationType} ${data.buttonAnimationSpeed || 2000}ms ease-in-out infinite`
                                    : data.buttonAnimation && data.buttonAnimation !== 'none'
                                    ? (() => {
                                        switch(data.buttonAnimation) {
                                            case 'pulse': return 'pulse 2s infinite';
                                            case 'bounce': return 'bounce 1s infinite';
                                            case 'shake': return 'shake 0.5s infinite';
                                            case 'glow': return 'glow 2s ease-in-out infinite alternate';
                                            default: return 'none';
                                        }
                                    })()
                                    : 'none',
                                
                                // ✅ SHADOW SYSTEM (wie HeroHeader) - KEIN Default-Shadow
                                boxShadow: data.buttonShadowEnabled 
                                    ? `0 ${data.buttonShadowDistance || 8}px ${data.buttonShadowIntensity || 15}px ${data.buttonShadowColor || '#000000'}${Math.round((data.buttonShadowOpacity || 25) * 2.55).toString(16).padStart(2, '0')}`
                                    : 'none',
                                
                                // ✅ LAYOUT PROPERTIES
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isEditMode ? 'pointer' : 'pointer',
                                transition: 'all 0.3s ease',
                            },
                            'Click to edit button text...',
                            'simplistic-quote-button'
                        )}
                    </div>
                ) : null
            }
        ];

        // Filtere null-Elemente raus und sortiere nach Order
        return elements
            .filter(element => element.component !== null)
            .sort((a, b) => a.order - b.order)
            .map(element => element.component);
    };

    // ✅ TEXT STYLES mit Font System und Max Width + Gap System
    const getTextContainerStyle = () => ({
        width: '100%',
        display: 'flex', // ✅ KRITISCH: Explizites Flex
        flexDirection: 'column', // ✅ KRITISCH: Column für Text-Stacking
        alignItems: 'center', // ✅ KRITISCH: Horizontale Zentrierung
        justifyContent: 'center', // ✅ KRITISCH: Vertikale Zentrierung
        gap: `${isMobileLayout ? (data.textGapMobile ?? data.textGap ?? 20) : (data.textGap ?? 25)}px`, // ✅ GAP SYSTEM: Responsive Gap zwischen Text-Elementen
        ...(data.enableTextMaxWidth && {
            maxWidth: `${data.textMaxWidth || 600}px`,
            margin: '0 auto' // Zentriert den Text-Container
        })
    });

    const categoryTextStyle = {
        color: data.categoryTextColor || '#999999',
        fontSize: `${isMobileLayout ? (data.categoryTextSizeMobile || 11) : (data.categoryTextSize || 12)}px`,
        fontWeight: data.categoryTextWeight || '500',
        fontFamily: data.categoryTextFont || fonts.globalSmallFont,
        lineHeight: '1.4',
        textTransform: 'uppercase', // ✅ Uppercase für Category-Look
        letterSpacing: '1px', // ✅ Letter-Spacing für bessere Lesbarkeit
        
        // ✅ INDIVIDUELLE MARGINS
        marginTop: `${data.categoryTextMarginTop ?? 0}px`,
        marginBottom: `${data.categoryTextMarginBottom ?? 0}px`,
        
        // ✅ ERWEITERTE STYLING-OPTIONEN für Category Text
        backgroundColor: data.categoryTextBackgroundColor || 'transparent',
        paddingLeft: `${data.categoryTextPaddingWidth || 0}px`,
        paddingRight: `${data.categoryTextPaddingWidth || 0}px`,
        paddingTop: `${data.categoryTextPaddingHeight || 0}px`,
        paddingBottom: `${data.categoryTextPaddingHeight || 0}px`,
        borderRadius: `${data.categoryTextBorderRadius || 0}px`,
        border: data.categoryTextBorderWidth > 0 ? `${data.categoryTextBorderWidth}px solid ${data.categoryTextBorderColor || '#cccccc'}` : 'none',
        display: 'inline-block', // ✅ Inline-block für Padding/Background
        
        ...getTextEffectStyles(data.categoryTextEffect || '', animationKey)
    };

    const simpleTextStyle = {
        color: data.simpleTextColor || '#666666',
        fontSize: `${isMobileLayout ? (data.simpleTextSizeMobile || 14) : (data.simpleTextSize || 14)}px`,
        fontWeight: data.simpleTextWeight || '400',
        fontFamily: data.simpleTextFont || fonts.globalSmallFont,
        lineHeight: '1.6',
        
        // ✅ INDIVIDUELLE MARGINS
        marginTop: `${data.simpleTextMarginTop ?? 0}px`,
        marginBottom: `${data.simpleTextMarginBottom ?? 0}px`,
        
        ...getTextEffectStyles(data.simpleTextEffect || '', animationKey)
    };

    const richTextStyle = {
        color: data.richTextColor || '#2c3e50',
        fontSize: `${isMobileLayout ? (data.richTextSizeMobile || 28) : (data.richTextSize || 44)}px`,
        fontWeight: data.richTextWeight || '600',
        fontFamily: data.richTextFont || fonts.globalBigFont,
        lineHeight: '1.4',
        
        // ✅ INDIVIDUELLE MARGINS
        marginTop: `${data.richTextMarginTop ?? 0}px`,
        marginBottom: `${data.richTextMarginBottom ?? 0}px`,
        
        ...getTextEffectStyles(data.richTextEffect || '', animationKey)
    };

    // ✅ BACKGROUND IMAGE EFFECT
    const getImageEffect = () => {
        const effect = data.imageEffect || 'none';
        switch (effect) {
            case 'darken':
                return { filter: 'brightness(0.7)' };
            case 'vignette':
                return { 
                    background: `
                        radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%),
                        url(${data.backgroundImage})
                    `,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                };
            case 'blur':
                return { filter: `blur(${data.backgroundImageBlur || 3}px)` };
            default:
                return {};
        }
    };

    return (
        <>
            <style>
                {`
                    /* ✅ TEXT_EDIT_README REQUIRED ANIMATIONS */
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    
                    
                    @keyframes simplicisticQuoteUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    /* SimplisticQuote Animation Keyframes (2025 Animation System) */
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    /* SimplisticQuote Animation Classes */
                    .quote-animated {
                        animation-fill-mode: both;
                        width: 100%;
                    }
                    
                    .quote-animated.fadeInUp {
                        animation-name: fadeInUp;
                    }
                    
                    .quote-animated.slideIn {
                        animation-name: slideIn;
                    }
                    
                    .quote-animated.fadeIn {
                        animation-name: fadeIn;
                    }
                    
                    /* ✅ BUTTON ANIMATIONS für ButtonWidgetStyle-Kompatibilität */
                    @keyframes pulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                    }
                    @keyframes bounce {
                        0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                        40% { transform: translateY(-10px); }
                        60% { transform: translateY(-5px); }
                    }
                    @keyframes shake {
                        0%, 100% { transform: translateX(0); }
                        10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
                        20%, 40%, 60%, 80% { transform: translateX(2px); }
                    }
                    @keyframes glow {
                        0% { box-shadow: 0 0 5px rgba(92, 128, 188, 0.5); }
                        100% { box-shadow: 0 0 20px rgba(92, 128, 188, 0.8), 0 0 30px rgba(92, 128, 188, 0.6); }
                    }
                    
                    /* ✅ GRADIENT ANIMATION */
                    @keyframes gradientShift {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    
                    /* ✅ GENTLE ANIMATIONS (ButtonWidgetStyle 2025) */
                    @keyframes gentle-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.02); opacity: 0.9; }
                    }
                    @keyframes gentle-sway {
                        0%, 100% { transform: translateX(0px); }
                        25% { transform: translateX(-2px); }
                        75% { transform: translateX(2px); }
                    }
                    @keyframes subtle-float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-3px); }
                    }
                    @keyframes gentle-breathe {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.03); opacity: 0.85; }
                    }
                    @keyframes gentle-wobble {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(-1deg); }
                        75% { transform: rotate(1deg); }
                    }
                    
                    /* ✅ FRAMELESS INLINE EDITING STYLES - TEXT_EDIT_README Rules */
                    .inline-editing-textarea {
                        font-family: inherit !important;
                        resize: none;
                        outline: none;
                    }
                    
                    .inline-editing-textarea:focus {
                        /* ✅ FRAMELESS: Kein Focus-Ring */
                        box-shadow: none;
                        outline: none;
                    }
                `}
            </style>
            
            <div 
                className={`widget-container simplistic-quote-widget simplistic-quote-container layout-${data.layoutType || 'centered-simple'}`}
                style={widgetStyle}
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
                {/* ✅ DELETE BUTTON (Edit Mode) */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'SimplisticQuote'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [SIMPLICISTICQUOTE] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}
                
                {/* ✅ DELETE MODAL */}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleDelete}
                    />
                )}
                
                {/* ✅ BACKGROUND IMAGE CONTAINER (nur wenn showBackgroundImage aktiv) */}
                {layoutConfig.showBackgroundImage && (
                    <div className="simplistic-quote-background-wrapper" style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        zIndex: 0 
                    }}>
                        <div className="simplistic-quote-background-image" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: `url(${data.backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: (data.backgroundImageOpacity || 80) / 100,
                            ...getImageEffect()
                        }} />
                    </div>
                )}

                {/* ✅ MAIN CONTENT CONTAINER - Vereinfacht für Column-Layout */}
                <div className="simplistic-quote-content-container" style={{ 
                    position: 'relative', 
                    zIndex: 1, 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: data.maxWidth ? appendPx(isMobileLayout ? 
                        (data.maxWidthMobile || data.maxWidth) : 
                        data.maxWidth
                    ) : '700px',
                    padding: isMobileLayout ? '20px' : '40px',
                    textAlign: getTextAlignment()
                }}>
                        
                        {/* ✅ TEXT WRAPPER mit Max Width - Dynamische Element-Reihenfolge */}
                        <div className="simplistic-quote-text-wrapper" style={getTextContainerStyle()}>
                            {renderElementsInOrder()}
                        </div> {/* ✅ TEXT WRAPPER Ende */}
                </div>
            </div>
        </>
    );
}

export default SimplisticQuote;